import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGameId,
  selectGameObject,
  selectGameStatusObject,
} from '../../gamestate/gameStateSlice';
import { selectAllPlayers } from '../../player/playerSlice';
import { selectNarrations, playNarration } from '../../host/hostStateSlice';
import { advanceState } from '../../gamestate/GameState';
import { updateGameStateAction } from '../../gamestate/stateActions';
import * as ttsService from '../../host/ttsService';
import BackgroundEffect from 'features/components/BackgroundEffect';
import logging from 'utils/logging';

const log = logging.getLogger('RoundIntroScreen');
log.setLevel('info');

const ACCENT_TEAL = '#2ec4b6';

const PHASES = {
  LOADING: 'LOADING',
  PLAYING_OPENING: 'PLAYING_OPENING',
  REVEALING_CATEGORIES: 'REVEALING_CATEGORIES',
  PLAYING_CLOSING: 'PLAYING_CLOSING',
  COMPLETE: 'COMPLETE',
};

const ROUND_DISPLAY_NAMES = {
  practice: 'Practice Round',
  round1: 'Round 1',
  round2: 'Double Groupardy',
  round3: 'Triple Groupardy',
};

const LOADING_TIMEOUT_MS = 5000;
const CATEGORY_PAUSE_MS = 400;
const VISUAL_ONLY_DELAY_MS = 1500;

// Animations
const glowPulse = keyframes`
  0% { box-shadow: 0 0 20px rgba(46, 196, 182, 0.6); }
  50% { box-shadow: 0 0 40px rgba(46, 196, 182, 0.9); }
  100% { box-shadow: 0 0 20px rgba(46, 196, 182, 0.6); }
`;

const titleFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled components
const Root = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
});

const RoundTitle = styled(Typography)({
  fontFamily: 'Gyparody, sans-serif',
  fontSize: '4rem',
  color: 'white',
  textAlign: 'center',
  marginBottom: '60px',
  textShadow: '2px 2px 8px rgba(0,0,0,0.5)',
  animation: `${titleFadeIn} 0.8s ease-out`,
});

const CategoriesRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  width: '90%',
  maxWidth: '1400px',
  height: '180px',
}));

const CategoryCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'revealed' && prop !== 'active',
})(({ theme, revealed, active }) => ({
  flex: 1,
  maxWidth: '220px',
  minWidth: '120px',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  backgroundColor: revealed ? ACCENT_TEAL : 'rgba(46, 196, 182, 0.15)',
  border: revealed ? 'none' : '2px solid rgba(46, 196, 182, 0.3)',
  opacity: revealed ? 1 : 0,
  transform: revealed ? 'scale(1)' : 'scale(0.8)',
  transition: 'opacity 0.5s ease-out, transform 0.5s ease-out, background-color 0.3s ease',
  ...(active && {
    animation: `${glowPulse} 1s ease-in-out infinite`,
  }),
}));

const CategoryText = styled(Typography)({
  fontFamily: 'Helvetica Inserat, Arial, sans-serif',
  fontSize: '1.8rem',
  color: 'white',
  textAlign: 'center',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  textShadow: '1px 1px 4px rgba(0,0,0,0.3)',
});

const RoundIntroScreen = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const game = useSelector(selectGameObject);
  const gameStatus = useSelector(selectGameStatusObject);
  const players = useSelector(selectAllPlayers);
  const narrations = useSelector(selectNarrations);

  const [phase, setPhase] = useState(PHASES.LOADING);
  const [revealedCategories, setRevealedCategories] = useState(new Set());
  const [activeCategory, setActiveCategory] = useState(-1);
  const mountedRef = useRef(true);
  const completedRef = useRef(false);
  const gameStatusRef = useRef(gameStatus);
  gameStatusRef.current = gameStatus;

  const round = gameStatus?.round;
  const categories = game?.categories?.[round] || [];
  const categoryNames = categories.map(c => c?.category).filter(Boolean);
  const roundDisplayName = ROUND_DISPLAY_NAMES[round] || round;

  // Get current revealedCount from game state for sync
  const roundIntro = gameStatus?.roundIntro;
  const syncedRevealedCount = roundIntro?.revealedCount || 0;

  // Find narrations by key
  const findNarration = useCallback((key) => {
    return narrations?.find(n => n.key === key);
  }, [narrations]);

  const openingNarration = findNarration(`ROUND_INTRO_${round}`);
  const closingNarration = findNarration(`ROUND_INTRO_CLOSING_${round}`);

  // Advance to waiting state
  const advanceToWaiting = useCallback(async () => {
    if (completedRef.current || !mountedRef.current) return;
    completedRef.current = true;
    log.info('Round intro complete, advancing to waiting');
    try {
      await advanceState(game, players, dispatch);
    } catch (error) {
      log.error('Error advancing state:', error);
    }
  }, [game, players, dispatch]);

  // Update revealedCount in game state for player sync
  const updateRevealedCount = useCallback((count) => {
    if (!gameId) return;
    dispatch(updateGameStateAction(gameId, {
      ...gameStatusRef.current,
      roundIntro: {
        revealedCount: count,
        totalCategories: categoryNames.length,
      },
    }));
  }, [gameId, categoryNames.length, dispatch]);

  // Mount logging and cleanup
  useEffect(() => {
    mountedRef.current = true;
    log.info(`RoundIntroScreen mounted: round=${round}, categories=${categoryNames.length}`);
    return () => {
      mountedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Page refresh resilience: if already partially revealed, restore state
  useEffect(() => {
    if (syncedRevealedCount > 0 && revealedCategories.size === 0) {
      const restored = new Set();
      for (let i = 0; i < syncedRevealedCount; i++) {
        restored.add(i);
      }
      setRevealedCategories(restored);
    }
  }, [syncedRevealedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize roundIntro in game state
  useEffect(() => {
    if (gameId && round && !roundIntro) {
      dispatch(updateGameStateAction(gameId, {
        ...gameStatusRef.current,
        roundIntro: {
          revealedCount: 0,
          totalCategories: categoryNames.length,
        },
      }));
    }
  }, [gameId, round]); // eslint-disable-line react-hooks/exhaustive-deps

  // LOADING phase: wait for opening narration or timeout
  useEffect(() => {
    if (phase !== PHASES.LOADING) return;

    log.info(`LOADING phase: openingAudio=${openingNarration?.audioStatus}, closingAudio=${closingNarration?.audioStatus}, syncedRevealed=${syncedRevealedCount}, categories=${categoryNames.length}`);

    // If we're refreshing mid-intro and all cats are revealed, skip to closing/complete
    if (syncedRevealedCount >= categoryNames.length && categoryNames.length > 0) {
      if (closingNarration?.audioStatus === 'READY') {
        log.info('All categories already revealed, playing closing');
        setPhase(PHASES.PLAYING_CLOSING);
      } else {
        log.info('All categories already revealed, completing');
        setPhase(PHASES.COMPLETE);
      }
      return;
    }

    // If we're refreshing mid-intro with some revealed, skip to revealing
    if (syncedRevealedCount > 0) {
      log.info(`Resuming from ${syncedRevealedCount} revealed categories`);
      setPhase(PHASES.REVEALING_CATEGORIES);
      return;
    }

    // Skip opening narration for practice round — InstructionsDone narration already covers it
    if (round === 'practice') {
      log.info('Practice round — skipping opening narration, going straight to category reveal');
      setPhase(PHASES.REVEALING_CATEGORIES);
      return;
    }

    // Check if opening narration is ready
    if (openingNarration?.audioStatus === 'READY') {
      log.info('Opening narration ready, playing');
      setPhase(PHASES.PLAYING_OPENING);
      return;
    }

    // Set timeout — skip to revealing categories (visual-only) if narration not ready
    const timeout = setTimeout(() => {
      if (mountedRef.current && phase === PHASES.LOADING) {
        log.warn('Loading timeout reached, skipping to visual-only category reveal');
        setPhase(PHASES.REVEALING_CATEGORIES);
      }
    }, LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [phase, openingNarration?.audioStatus, syncedRevealedCount, categoryNames.length, closingNarration?.audioStatus, round]);

  // PLAYING_OPENING phase
  useEffect(() => {
    if (phase !== PHASES.PLAYING_OPENING) return;
    if (!openingNarration || openingNarration.audioStatus !== 'READY') {
      log.info('Opening narration not ready, skipping to reveal');
      setPhase(PHASES.REVEALING_CATEGORIES);
      return;
    }
    log.info('Playing opening narration');

    dispatch(playNarration({
      gameId,
      narration: openingNarration,
      onComplete: () => {
        if (mountedRef.current) {
          setPhase(PHASES.REVEALING_CATEGORIES);
        }
      }
    }));
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // REVEALING_CATEGORIES phase
  useEffect(() => {
    if (phase !== PHASES.REVEALING_CATEGORIES) return;

    const startIndex = syncedRevealedCount;
    log.info(`Starting category reveal from index ${startIndex}, total ${categoryNames.length}`);

    const revealSequence = async () => {
      for (let i = startIndex; i < categoryNames.length; i++) {
        if (!mountedRef.current) return;

        // Set active category for glow effect
        setActiveCategory(i);

        // Reveal this category
        setRevealedCategories(prev => new Set([...prev, i]));

        // Update synced count for player devices
        updateRevealedCount(i + 1);

        // Try to play category audio
        const catNarration = findNarration(`CATEGORY_AUDIO_${round}_${i}`);
        if (catNarration?.audioStatus === 'READY' && catNarration?.audioURL) {
          try {
            const { promise } = await ttsService.playAudio(catNarration.audioURL);
            await promise;
          } catch (error) {
            log.warn(`Error playing category audio for index ${i}:`, error);
            // Fall back to visual-only delay
            await new Promise(r => setTimeout(r, VISUAL_ONLY_DELAY_MS));
          }
        } else {
          // No audio available — visual-only reveal with timed delay
          await new Promise(r => setTimeout(r, VISUAL_ONLY_DELAY_MS));
        }

        // Clear active glow
        setActiveCategory(-1);

        // Pause between categories
        if (i < categoryNames.length - 1) {
          await new Promise(r => setTimeout(r, CATEGORY_PAUSE_MS));
        }
      }

      if (mountedRef.current) {
        setPhase(PHASES.PLAYING_CLOSING);
      }
    };

    revealSequence().catch(error => {
      log.error('revealSequence failed:', error);
      if (mountedRef.current) setPhase(PHASES.PLAYING_CLOSING);
    });
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // PLAYING_CLOSING phase
  useEffect(() => {
    if (phase !== PHASES.PLAYING_CLOSING) return;

    if (!closingNarration || closingNarration.audioStatus !== 'READY') {
      // Skip if not ready, wait a moment then complete
      const timer = setTimeout(() => {
        if (mountedRef.current) {
          setPhase(PHASES.COMPLETE);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }

    dispatch(playNarration({
      gameId,
      narration: closingNarration,
      onComplete: () => {
        if (mountedRef.current) {
          setPhase(PHASES.COMPLETE);
        }
      }
    }));
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // COMPLETE phase
  useEffect(() => {
    if (phase !== PHASES.COMPLETE) return;
    advanceToWaiting();
  }, [phase, advanceToWaiting]);

  return (
    <Root>
      <BackgroundEffect />
      <RoundTitle variant="h1">
        {roundDisplayName}
      </RoundTitle>
      <CategoriesRow>
        {categoryNames.map((name, idx) => (
          <CategoryCell
            key={`cat-${idx}`}
            revealed={revealedCategories.has(idx)}
            active={activeCategory === idx}
          >
            {revealedCategories.has(idx) && (
              <CategoryText>
                {name}
              </CategoryText>
            )}
          </CategoryCell>
        ))}
      </CategoriesRow>
    </Root>
  );
};

export default RoundIntroScreen;
